import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import AppTile from "../components/AppTile";
import EditAppModal from "../components/EditAppModal";
import AppsVisibilityMatrix from "../components/AppsVisibilityMatrix";
import {
  Plus, Search, Shield, ChevronDown, ChevronRight, Globe, Lock, Pencil, Check, ChevronUp, ChevronDown as ChevronDownIcon,
} from "lucide-react";
import { cn } from "../lib/utils";

function CollapsibleSection({ title, icon: Icon, scoped, count, defaultOpen = true, actions, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-border bg-card overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/40 transition-colors"
      >
        <div className="w-7 h-7 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-primary" />
        </div>
        <h3 className="text-[15px] font-semibold">{title}</h3>
        {scoped && (
          <span className="inline-flex items-center gap-1 rounded-full border border-primary/30 bg-primary/10 text-primary text-[10px] font-semibold px-2 py-0.5 uppercase tracking-wide">
            <Lock className="w-3 h-3" /> Scoped
          </span>
        )}
        <span className="text-[11.5px] text-muted-foreground ml-1">{count} apps</span>
        <div className="ml-auto flex items-center gap-2">
          {actions && <div onClick={(e) => e.stopPropagation()}>{actions}</div>}
          <span className="text-muted-foreground">
            {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </span>
        </div>
      </button>
      {open && (
        <div className="border-t border-border p-4">
          {children}
        </div>
      )}
    </section>
  );
}

export default function AppsDirectory() {
  const { user } = useAuth();
  const { toast } = useToast();
  const isAdmin = user?.role === "Super Admin";
  const canManageVisibility = isAdmin || (Array.isArray(user?.scopes) && user.scopes.includes("administrator"));

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [ordered, setOrdered] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard");
      setApps(data.apps || []);
    } catch {
      toast({ title: "Failed to load apps", variant: "destructive" });
    } finally { setLoading(false); }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (editMode) setOrdered([...apps]);
    if (!editMode) setOrdered(null);
  }, [editMode, apps]);

  const moveApp = (id, dir) => {
    if (!ordered) return;
    const idx = ordered.findIndex((a) => a.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= ordered.length) return;
    const next = [...ordered];
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrdered(next);
  };

  const saveOrder = async () => {
    if (!ordered) { setEditMode(false); return; }
    setSavingOrder(true);
    try {
      const payload = ordered.map((a, i) => ({ id: a.id, display_order: (i + 1) * 10 }));
      await api.post("/admin/apps/reorder", payload);
      toast({ title: "App order saved" });
      setEditMode(false);
      await load();
    } catch (e) {
      toast({ title: "Save failed", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSavingOrder(false); }
  };

  const filterFn = (list) => (
    !query.trim() ? list :
      list.filter((a) => (a.name + " " + (a.description || "")).toLowerCase().includes(query.toLowerCase()))
  );

  const mainList = editMode && ordered ? ordered : apps;
  const filteredMain = filterFn(mainList);

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[12px] text-muted-foreground inline-flex items-center gap-1">
            <span className="hover:text-foreground cursor-pointer">Dashboard</span>
            <span>›</span>
            <span className="text-foreground font-medium">Apps</span>
          </div>
          <h1 className="text-[26px] font-semibold tracking-tight mt-1">Apps</h1>
          <p className="text-[13px] text-muted-foreground">Application launcher.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search apps…" className="pl-9 h-10 w-64" />
          </div>
        </div>
      </div>

      {/* Apps section — collapsible, icon view */}
      <CollapsibleSection
        title="Apps"
        icon={Globe}
        count={filteredMain.length}
        defaultOpen={true}
        actions={isAdmin && (
          editMode ? (
            <>
              <Button variant="ghost" size="sm" className="h-8" onClick={() => setEditMode(false)} disabled={savingOrder}>Cancel</Button>
              <Button size="sm" className="h-8 gap-1" onClick={saveOrder} disabled={savingOrder}><Check className="w-3.5 h-3.5" /> {savingOrder ? "Saving…" : "Done"}</Button>
            </>
          ) : (
            <Button variant="ghost" size="sm" className="h-8 gap-1" onClick={() => setEditMode(true)}><Pencil className="w-3.5 h-3.5" /> Edit order</Button>
          )
        )}
      >
        {loading ? (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
            {[...Array(10)].map((_, i) => <div key={i} className="aspect-square rounded-lg bg-muted/40 animate-pulse" />)}
          </div>
        ) : filteredMain.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground text-[13px]">No apps match “{query}”.</div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
            {filteredMain.map((app) => (
              <div key={app.id} className="relative group">
                <AppTile app={app} variant="compact" />
                {editMode && isAdmin && (
                  <>
                    <div className="absolute inset-0 rounded-lg ring-2 ring-primary/50 bg-primary/5 cursor-move" />
                    <div className="absolute -top-1.5 -right-1.5 z-10 flex gap-0.5">
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveApp(app.id, -1); }}
                        className="h-6 w-6 rounded-full bg-background border border-primary/40 shadow-sm inline-flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground">
                        <ChevronUp className="w-3 h-3" />
                      </button>
                      <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); moveApp(app.id, 1); }}
                        className="h-6 w-6 rounded-full bg-background border border-primary/40 shadow-sm inline-flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground">
                        <ChevronDownIcon className="w-3 h-3" />
                      </button>
                    </div>
                  </>
                )}
                {isAdmin && !editMode && (
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setEditing(app); }}
                    className="absolute bottom-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 rounded-full bg-primary text-primary-foreground shadow-lg inline-flex items-center justify-center hover:bg-primary/90"
                    title="Edit app">
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Productivity section removed 2026-07-30 */}

      {canManageVisibility && <AppsVisibilityMatrix />}

      {editing && (
        <EditAppModal
          app={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDelete={editing.id && !editing.built_in ? async () => {
            try {
              await api.delete(`/admin/apps/${editing.id}`);
              toast({ title: `Deleted ${editing.name}` });
              setEditing(null); load();
            } catch (e) {
              toast({ title: "Delete failed", description: e?.response?.data?.detail || "", variant: "destructive" });
            }
          } : null}
        />
      )}
    </div>
  );
}
